import React, { useEffect, useState, useCallback } from "react";
import {
  Box, Typography, TextField, Button, Alert, CircularProgress,
  Paper, Divider, Stack,
  Table, TableHead, TableBody, TableRow, TableCell, TableContainer,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import axios from "../../../config/axiosConfig";
import { useAuth } from "../../../context/AuthContext";
import { CAJA_VIATICO_COLOR } from "../theme";
import { esRolAdminGerenteContable } from "../permisos";
import { fieldSx as compactFieldSx } from "../../egresos/components/formStyles";

const Th = styled(TableCell)(({ theme }) => ({
  fontWeight: 700,
  fontSize: "0.78rem",
  padding: "3px 6px",
  color: theme.palette.mode === "dark" ? theme.palette.common.white : CAJA_VIATICO_COLOR,
  // Opaco (no `${CAJA_VIATICO_COLOR}14`, que es semi-transparente): con stickyHeader,
  // un fondo transparente deja ver las filas de abajo a través del header al hacer
  // scroll.
  backgroundColor: theme.palette.mode === "dark" ? theme.palette.grey[800] : "#ebf3f2",
}));

const initialForm = { descripcion: "", detalle: "" };

const RegistroItemsTab = () => {
  const { user } = useAuth();
  const puedeRegistrar = esRolAdminGerenteContable(user);

  const [items, setItems] = useState([]);
  const [loadingLista, setLoadingLista] = useState(true);
  const [errorLista, setErrorLista] = useState("");

  const [form, setForm] = useState(initialForm);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const cargarLista = useCallback(() => {
    setLoadingLista(true);
    setErrorLista("");
    axios.get("/api/caja-viatico/items/detalle")
      .then((res) => setItems(res.data?.data || []))
      .catch((e) => setErrorLista(e?.response?.data?.message || "Error al cargar los items."))
      .finally(() => setLoadingLista(false));
  }, []);

  useEffect(() => {
    cargarLista();
  }, [cargarLista]);

  const handleChange = (campo) => (e) => setForm((prev) => ({ ...prev, [campo]: e.target.value }));

  const handleClear = () => {
    setForm(initialForm);
    setError("");
  };

  const handleGuardar = async () => {
    setSaving(true);
    setError("");
    try {
      await axios.post("/api/caja-viatico/items", {
        descripcion: form.descripcion.trim(),
        detalle: form.detalle.trim(),
      });
      setForm(initialForm);
      cargarLista();
    } catch (e) {
      setError(e?.response?.data?.message || "Error al registrar el item.");
    } finally {
      setSaving(false);
    }
  };

  const puedeGuardar = form.descripcion.trim() && !saving;

  return (
    <Box>
      <Typography variant="h6" sx={{ fontWeight: 800, mb: 1 }}>
        Listado de Items de Caja Viático
      </Typography>

      {errorLista && <Alert severity="error" sx={{ mb: 1 }}>{errorLista}</Alert>}

      {loadingLista ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}><CircularProgress size={22} /></Box>
      ) : items.length === 0 ? (
        <Alert severity="info" sx={{ mb: 1.5 }}>No existen datos.</Alert>
      ) : (
        <Paper variant="outlined" sx={{ mb: 1.5 }}>
          <TableContainer sx={{ maxHeight: "45vh" }}>
            <Table size="small" stickyHeader sx={{ "& td": { py: 0.4, px: 0.75, fontSize: "0.78rem" } }}>
              <TableHead>
                <TableRow>
                  <Th>Item</Th>
                  <Th>Descripción</Th>
                  <Th>Detalle</Th>
                </TableRow>
              </TableHead>
              <TableBody>
                {items.map((it) => (
                  <TableRow key={it.id} hover>
                    <TableCell>{it.id}</TableCell>
                    <TableCell>{it.descripcion}</TableCell>
                    <TableCell>{it.detalle || "-"}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {puedeRegistrar && (
        <>
          <Divider sx={{ mb: 1.5 }} />

          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 1.5, color: CAJA_VIATICO_COLOR }}>
              Nuevo Item
            </Typography>
            {error && <Alert severity="error" sx={{ mb: 1.5 }}>{error}</Alert>}

            <Stack spacing={0.6}>
              <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 2fr" }, gap: 0.6 }}>
                <TextField
                  size="small" label="Descripción" required
                  value={form.descripcion} onChange={handleChange("descripcion")}
                  sx={compactFieldSx}
                />
                <TextField
                  size="small" label="Detalle"
                  value={form.detalle} onChange={handleChange("detalle")}
                  sx={compactFieldSx}
                />
              </Box>

              <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
                <Button onClick={handleClear} disabled={saving}>Borrar</Button>
                <Button
                  variant="contained" onClick={handleGuardar} disabled={!puedeGuardar}
                  sx={{ bgcolor: CAJA_VIATICO_COLOR, "&:hover": { bgcolor: "#004d40" } }}
                >
                  {saving ? "Guardando..." : "Guardar"}
                </Button>
              </Box>
            </Stack>
          </Paper>
        </>
      )}
    </Box>
  );
};

export default RegistroItemsTab;
